import React, { useMemo, useState, useRef, useCallback, useTransition } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import XLSX from "xlsx-js-style";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "@mui/material";
import { Dialog } from "primereact/dialog";
import useGetDataUser from "../../../../utilidades/customHooks/useGetDataUser";


// ── IDs de usuario con permiso para editar en la tabla principal ─────────────
const PERMISOS_EDICION = [4465,4518,1,2,5017];


const DIAS = {
    Monday: "LUNES", Tuesday: "MARTES", Wednesday: "MIÉRCOLES",
    Thursday: "JUEVES", Friday: "VIERNES", Saturday: "SÁBADO", Sunday: "DOMINGO",
};
const DIA_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmt$ = (v) =>
    v != null && v !== ""
        ? new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v)
        : "";

const agrupar = (data) => {
    const map = new Map();
    for (const r of data) {
        const ruta = r.RUTA?.trim() ?? "";
        const turnoNum = r.TURNO?.slice(-1) ?? "";
        const key = `${r.DIA}|${r.FECHA}|${ruta}|${turnoNum}`;
        if (!map.has(key))
            map.set(key, { DIA: r.DIA, FECHA: r.FECHA, RUTA: ruta, turnoNum, TIPO_UNIDAD: "", eRecords: [], sRecords: [] });
        const grp = map.get(key);
        if (r.TURNO?.startsWith("E")) {
            grp.eRecords.push(r);
            grp.TIPO_UNIDAD = r["TIPO DE UNIDAD"];
        } else if (r.TURNO?.startsWith("S")) {
            grp.sRecords.push(r);
            if (!grp.TIPO_UNIDAD) grp.TIPO_UNIDAD = r["TIPO DE UNIDAD"];
        }
    }
    // Expandir cada grupo: E[i] pareado con S[i], filas individuales
    const rows = [];
    for (const grp of map.values()) {
        const len = Math.max(grp.eRecords.length, grp.sRecords.length, 1);
        for (let i = 0; i < len; i++) {
            const e = grp.eRecords[i] ?? null;
            const s = grp.sRecords[i] ?? null;
            rows.push({
                DIA: grp.DIA,
                FECHA: grp.FECHA,
                RUTA: grp.RUTA,
                turnoNum: grp.turnoNum,
                TIPO_UNIDAD: grp.TIPO_UNIDAD,
                e,
                s,
                HORA_ARRANQUE: e?.['HORA DE ARRANQUE'] || s?.['HORA DE ARRANQUE'] || null,
                eCosto: e?.COSTO ?? 0,
                sCosto: s?.COSTO ?? 0,
                eBitacoras: e?.BITACORA ? [e.BITACORA] : [],
                sBitacoras: s?.BITACORA ? [s.BITACORA] : [],
                excepcion: !!(e?.EXCEPCION || s?.EXCEPCION),
            });
        }
    }
    return rows.sort(
        (a, b) =>
            DIA_ORDER.indexOf(a.DIA) - DIA_ORDER.indexOf(b.DIA) ||
            a.FECHA.localeCompare(b.FECHA) ||
            a.turnoNum.localeCompare(b.turnoNum) ||
            a.RUTA.localeCompare(b.RUTA)
    );
};

const calcSpans = (rows) => {
    const spans = new Array(rows.length).fill(0);
    let i = 0;
    while (i < rows.length) {
        let j = i + 1;
        while (j < rows.length && rows[j].DIA === rows[i].DIA) j++;
        spans[i] = j - i;
        i = j;
    }
    return spans;
};

// ── Estilos de inputs (definidos fuera para no recrear en cada render) ──────
const inNum  = { width: '75px',  padding: '3px 5px', fontSize: '0.78rem', border: '1px solid #ced4da', borderRadius: '3px', background: '#fff' };
const inTime = { width: '162px', padding: '3px 5px', fontSize: '0.78rem', border: '1px solid #ced4da', borderRadius: '3px', background: '#fff' };


// Extrae "HH:mm" de cualquier formato: "HH:mm", "HH:mm:ss", "yyyy-MM-dd HH:mm:ss", "yyyy-MM-ddTHH:mm:ss"
const extractTime = (val) => {
    if (!val) return '';
    const s = String(val);
    // Si tiene separador de fecha (espacio o T), tomar la parte de tiempo
    const sep = s.includes('T') ? 'T' : ' ';
    const timePart = s.includes(sep) ? s.split(sep)[1] : s;
    return timePart.slice(0, 5); // "HH:mm"
};

// Parsea "DD/MM/YYYY HH:mm:ss" → Date local. Devuelve null si el formato es inválido.
const parseDDMMYYYY = (str) => {
    if (!str) return null;
    const s = String(str).trim();
    const spaceIdx = s.indexOf(' ');
    const fechaPart = spaceIdx === -1 ? s : s.slice(0, spaceIdx);
    const timePart  = spaceIdx === -1 ? '0:0:0' : s.slice(spaceIdx + 1);
    const [d, m, y] = fechaPart.split('/').map(Number);
    const [hh = 0, mm = 0, ss = 0] = timePart.split(':').map(Number);
    const fecha = new Date(y, m - 1, d, hh, mm, ss);
    return isNaN(fecha.getTime()) ? null : fecha;
};

// Devuelve true si el registro todavía no debe editarse (es futuro).
// 1. Si HORA_ARRANQUE existe → compara fecha+hora exacta con ahora.
// 2. Si HORA_ARRANQUE es nulo (ruta aún no ha arrancado) → usa FECHA (DD/MM/YYYY)
//    y bloquea si el día completo es posterior a hoy.
const esFuturo = (horaArranque, fechaDDMMYYYY) => {
    if (horaArranque) {
        const fecha = parseDDMMYYYY(horaArranque);
        return fecha !== null && fecha > new Date();
    }
    if (fechaDDMMYYYY) {
        const [d, m, y] = fechaDDMMYYYY.split('/').map(Number);
        const ahora = new Date();
        const hoy   = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
        return new Date(y, m - 1, d) > hoy;
    }
    return false;
};

/**
 * Fila de la tabla "No ejecutados".
 * Inputs NO CONTROLADOS (defaultValue + ref) → escribir no dispara re-renders del padre.
 * React.memo → solo re-renderiza si cambian sus props.
 */
const NERow = React.memo(function NERow({ row, editing, savedE, savedS, setEditsNE, setEditingRowsNE, onGuardar }) {
    const { e, s } = row;
    const total = (e?.COSTO ?? 0) + (s?.COSTO ?? 0);
    const rKey = `${row.DIA}|${row.FECHA}|${row.RUTA}|${row.turnoNum}|${e?.BITACORA ?? ""}|${s?.BITACORA ?? ""}`;

    const eUnidadRef    = useRef();
    const eAforoRef     = useRef();
    const eHoraRef      = useRef();
    const eTerminadoRef = useRef();
    const sUnidadRef    = useRef();
    const sAforoRef     = useRef();
    const sTerminadoRef = useRef();

    const getE = (field) => savedE?.[field] ?? e?.[field] ?? "";
    const getS = (field) => savedS?.[field] ?? s?.[field] ?? "";

    const handleToggle = useCallback(() => {
        setEditingRowsNE(prev => {
            const next = new Set(prev);
            if (next.has(rKey)) next.delete(rKey); else next.add(rKey);
            return next;
        });
    }, [rKey, setEditingRowsNE]);

    const handleSave = useCallback(() => {
        const toNum = (ref) => ref.current?.value !== "" ? Number(ref.current?.value) : null;
        // ref contiene sólo la hora "HH:mm"; se combina con la fecha de la fila
        const toDateTime = (ref) => {
            const v = ref.current?.value; // "HH:mm"
            if (!v) return null;
            // row.FECHA viene como "DD/MM/YYYY" → convertir a "YYYY-MM-DD"
            const fechaISO = row.FECHA.split('/').reverse().join('-');
            return `${fechaISO} ${v}:00`;
        };

        // ── Payload para la API ───────────────────────────────────────
        const eUnidad    = toNum(eUnidadRef);
        const eAforo     = toNum(eAforoRef);
        const eHoraInput = toDateTime(eHoraRef);
        // Usar valor existente como respaldo si el input de hora quedó vacío
        const eHora      = eHoraInput ?? (getE('HORA DE LLEGADA') || null);
        const sUnidad    = toNum(sUnidadRef);
        const sAforo     = toNum(sAforoRef);

        const eTerminado = eTerminadoRef.current?.checked ?? false;
        const sTerminado = sTerminadoRef.current?.checked ?? false;

        // HORA DE LLEGADA no es obligatoria para guardar
        const eCompleto  = e && eUnidad != null && eAforo != null && eTerminado;
        const sCompleto  = s && sUnidad != null && sAforo != null && sTerminado;

        const apiPayload = [
            ...(eCompleto ? [{
                tipo: 'ENTRADA',
                bitacora: e.BITACORA ?? null,
                campos: {
                    UNIDAD:       eUnidad,
                    AFORO:        eAforo,
                    ...(eHora ? { HORA_LLEGADA: eHora } : {}),
                    TERMINADO:    1,
                },
            }] : []),
            ...(sCompleto ? [{
                tipo: 'SALIDA',
                bitacora: s.BITACORA ?? null,
                campos: {
                    UNIDAD:    sUnidad,
                    AFORO:     sAforo,
                    TERMINADO: 1,
                },
            }] : []),
        ];
        if (apiPayload.length > 0) onGuardar?.(apiPayload);

        // ── Guardar en estado local ───────────────────────────────────
        setEditsNE(prev => ({
            ...prev,
            [rKey]: {
                e: { UNIDAD: eUnidadRef.current?.value ?? "", AFORO: eAforoRef.current?.value ?? "", 'HORA DE LLEGADA': eHoraRef.current?.value ?? "", TERMINADO: eTerminadoRef.current?.checked ? 1 : 0 },
                s: { UNIDAD: sUnidadRef.current?.value ?? "", AFORO: sAforoRef.current?.value ?? "", TERMINADO: sTerminadoRef.current?.checked ? 1 : 0 },
            },
        }));
        setEditingRowsNE(prev => { const n = new Set(prev); n.delete(rKey); return n; });
    }, [rKey, setEditsNE, setEditingRowsNE, onGuardar, e, s, savedE, savedS]);

    return (
        <tr>
            <td className="td-dia">{DIAS[row.DIA] ?? row.DIA}</td>
            <td>{row.FECHA}</td>
            <td>Turno {row.turnoNum}.</td>
            <td>{row.TIPO_UNIDAD ?? ""}</td>
            <td className="td-ruta">
                {row.RUTA}
                <div style={{ marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {e?.BITACORA && (
                        <span style={{ fontSize: '0.68rem', color: '#0078D4', background: '#EFF6FC', border: '1px solid #C7E0F4', borderRadius: 3, padding: '0 5px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                            <i className="pi pi-sign-in" style={{ fontSize: '0.6rem', marginRight: 2 }} />{e.BITACORA}
                        </span>
                    )}
                    {s?.BITACORA && (
                        <span style={{ fontSize: '0.68rem', color: '#7B5EA7', background: '#F5F0FF', border: '1px solid #D4B8F0', borderRadius: 3, padding: '0 5px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                            <i className="pi pi-sign-out" style={{ fontSize: '0.6rem', marginRight: 2 }} />{s.BITACORA}
                        </span>
                    )}
                </div>
            </td>

            {/* ENTRADA */}
            {e == null ? (
                <td colSpan={5} style={{ textAlign: 'center', background: '#f0f4f8', color: '#2563a4', fontStyle: 'italic', fontSize: '0.78rem' }}>
                    <i className="pi pi-ban" style={{ marginRight: 5, fontSize: '0.8rem' }} />
                    Sin registro de entrada
                </td>
            ) : (
                <>
                    <td>{editing ? <input type="number" ref={eUnidadRef} defaultValue={getE('UNIDAD')} style={inNum} /> : (getE('UNIDAD') || "")}</td>
                    <td>{editing ? <input type="number" ref={eAforoRef}  defaultValue={getE('AFORO')}  style={inNum} /> : (getE('AFORO')  || "")}</td>
                    <td>{editing
                        ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: '0.78rem', color: '#555', whiteSpace: 'nowrap' }}>{row.FECHA}</span>
                            <input
                                type="time"
                                ref={eHoraRef}
                                defaultValue={extractTime(getE('HORA DE LLEGADA'))}
                                style={{ width: '90px', padding: '3px 5px', fontSize: '0.78rem', border: '1px solid #ced4da', borderRadius: '3px', background: '#fff' }}
                            />
                          </span>
                        : (getE('HORA DE LLEGADA') || "")}</td>
                    <td style={{ textAlign: 'center' }}>
                        {editing
                            ? <input type="checkbox" ref={eTerminadoRef} defaultChecked={!!getE('TERMINADO')} />
                            : <i className={getE('TERMINADO') ? 'pi pi-check-circle' : 'pi pi-circle'} style={{ color: getE('TERMINADO') ? '#107C10' : '#bdbdbd', fontSize: '1rem' }} />}
                    </td>
                    <td className="td-num">{fmt$(e?.COSTO)}</td>
                </>
            )}

            {/* SALIDA */}
            {s == null ? (
                <td colSpan={4} style={{ textAlign: 'center', background: '#f8f0f0', color: '#a94442', fontStyle: 'italic', fontSize: '0.78rem' }}>
                    <i className="pi pi-ban" style={{ marginRight: 5, fontSize: '0.8rem' }} />
                    Sin registro de salida
                </td>
            ) : (
                <>
                    <td>{editing ? <input type="number" ref={sUnidadRef} defaultValue={getS('UNIDAD')} style={inNum} /> : (getS('UNIDAD') || "")}</td>
                    <td>{editing ? <input type="number" ref={sAforoRef}  defaultValue={getS('AFORO')}  style={inNum} /> : (getS('AFORO')  || "")}</td>
                    <td style={{ textAlign: 'center' }}>
                        {editing
                            ? <input type="checkbox" ref={sTerminadoRef} defaultChecked={!!getS('TERMINADO')} />
                            : <i className={getS('TERMINADO') ? 'pi pi-check-circle' : 'pi pi-circle'} style={{ color: getS('TERMINADO') ? '#107C10' : '#bdbdbd', fontSize: '1rem' }} />}
                    </td>
                    <td className="td-num">{fmt$(s?.COSTO)}</td>
                </>
            )}
            <td className="td-num td-total">{total ? fmt$(total) : ""}</td>

            {/* ACCIONES */}
            <td style={{ textAlign: 'center' }}>
                {!esFuturo(row.HORA_ARRANQUE, row.FECHA) && (
                    <Button
                        icon={editing ? 'pi pi-check' : 'pi pi-pencil'}
                        size="small"
                        severity={editing ? 'success' : 'info'}
                        rounded text
                        tooltip={editing ? 'Guardar' : 'Editar'}
                        tooltipOptions={{ position: 'left' }}
                        onClick={editing ? handleSave : handleToggle}
                    />
                )}
            </td>
        </tr>
    );
});

/**
 * Fila de la tabla principal con edición inline.
 * Solo se puede editar si TERMINADO es falso en entrada o salida.
 * La columna ACCIONES solo aparece si canEditMain es true.
 */
const MainRow = React.memo(function MainRow({ row, spanDia, editing, savedE, savedS, setEditsMain, setEditingRowsMain, onGuardar, canEditMain }) {
    const { e, s } = row;
    const total = (e?.COSTO ?? 0) + (s?.COSTO ?? 0);
    const rKey = `${row.DIA}|${row.FECHA}|${row.RUTA}|${row.turnoNum}|${e?.BITACORA ?? ""}|${s?.BITACORA ?? ""}`;

    const getE = (field) => savedE?.[field] ?? e?.[field] ?? "";
    const getS = (field) => savedS?.[field] ?? s?.[field] ?? "";

    const eEditable = e != null && !getE('TERMINADO');
    const sEditable = s != null && !getS('TERMINADO');
    const canEdit   = !esFuturo(row.HORA_ARRANQUE, row.FECHA) && (eEditable || sEditable);

    const eUnidadRef    = useRef();
    const eAforoRef     = useRef();
    const eHoraRef      = useRef();
    const eTerminadoRef = useRef();
    const sUnidadRef    = useRef();
    const sAforoRef     = useRef();
    const sTerminadoRef = useRef();

    const handleToggle = useCallback(() => {
        setEditingRowsMain(prev => {
            const next = new Set(prev);
            if (next.has(rKey)) next.delete(rKey); else next.add(rKey);
            return next;
        });
    }, [rKey, setEditingRowsMain]);

    const handleSave = useCallback(() => {
        const toNum = (ref) => ref.current?.value !== "" ? Number(ref.current?.value) : null;
        const toDateTime = (ref) => {
            const v = ref.current?.value;
            if (!v) return null;
            const fechaISO = row.FECHA.split('/').reverse().join('-');
            return `${fechaISO} ${v}:00`;
        };

        const eUnidad    = eEditable ? toNum(eUnidadRef) : null;
        const eAforo     = eEditable ? toNum(eAforoRef) : null;
        const eHoraInput = eEditable ? toDateTime(eHoraRef) : null;
        // Usar valor existente como respaldo si el input de hora quedó vacío
        const eHora      = eHoraInput ?? (eEditable ? (getE('HORA DE LLEGADA') || null) : null);
        const eTerminado = eEditable ? (eTerminadoRef.current?.checked ?? false) : false;
        const sUnidad    = sEditable ? toNum(sUnidadRef) : null;
        const sAforo     = sEditable ? toNum(sAforoRef) : null;
        const sTerminado = sEditable ? (sTerminadoRef.current?.checked ?? false) : false;

        // HORA DE LLEGADA no es obligatoria para guardar
        const eCompleto = eEditable && eUnidad != null && eAforo != null && eTerminado;
        const sCompleto = sEditable && sUnidad != null && sAforo != null && sTerminado;

        const apiPayload = [
            ...(eCompleto ? [{
                tipo: 'ENTRADA',
                bitacora: e.BITACORA ?? null,
                campos: { UNIDAD: eUnidad, AFORO: eAforo, ...(eHora ? { HORA_LLEGADA: eHora } : {}), TERMINADO: 1 },
            }] : []),
            ...(sCompleto ? [{
                tipo: 'SALIDA',
                bitacora: s.BITACORA ?? null,
                campos: { UNIDAD: sUnidad, AFORO: sAforo, TERMINADO: 1 },
            }] : []),
        ];
        if (apiPayload.length > 0) onGuardar?.(apiPayload);

        setEditsMain(prev => ({
            ...prev,
            [rKey]: {
                e: eEditable
                    ? { UNIDAD: eUnidadRef.current?.value ?? "", AFORO: eAforoRef.current?.value ?? "", 'HORA DE LLEGADA': eHoraRef.current?.value ?? "", TERMINADO: eTerminadoRef.current?.checked ? 1 : 0 }
                    : prev[rKey]?.e,
                s: sEditable
                    ? { UNIDAD: sUnidadRef.current?.value ?? "", AFORO: sAforoRef.current?.value ?? "", TERMINADO: sTerminadoRef.current?.checked ? 1 : 0 }
                    : prev[rKey]?.s,
            },
        }));
        setEditingRowsMain(prev => { const n = new Set(prev); n.delete(rKey); return n; });
    }, [rKey, e, s, eEditable, sEditable, setEditsMain, setEditingRowsMain, onGuardar, row.FECHA]);

    return (
        <tr style={row.excepcion ? { background: '#fff3cd' } : undefined}>
            {spanDia > 0 && (
                <td rowSpan={spanDia} className="td-dia">
                    {DIAS[row.DIA] ?? row.DIA}
                </td>
            )}
            <td>{row.FECHA}</td>
            <td>Turno {row.turnoNum}.</td>
            <td>{row.TIPO_UNIDAD}</td>
            <td className="td-ruta">{row.RUTA}</td>
            <td style={{ textAlign: 'center' }}>
                {row.excepcion && (
                    <Tooltip title="Excepción" placement="top">
                        <i className="pi pi-exclamation-circle" style={{ color: '#856404', fontSize: '1rem' }} />
                    </Tooltip>
                )}
            </td>

            {/* ENTRADA */}
            <td style={{ fontSize: '0.75rem', color: '#555' }}>{row.eBitacoras.join(", ")}</td>
            <td>{eEditable && editing ? <input type="number" ref={eUnidadRef} defaultValue={getE('UNIDAD')} style={inNum} /> : (getE('UNIDAD') || "")}</td>
            <td>{eEditable && editing ? <input type="number" ref={eAforoRef}  defaultValue={getE('AFORO')}  style={inNum} /> : (getE('AFORO')  || "")}</td>
            <td>
                {eEditable && editing
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: '0.78rem', color: '#555', whiteSpace: 'nowrap' }}>{row.FECHA}</span>
                        <input type="time" ref={eHoraRef} defaultValue={extractTime(getE('HORA DE LLEGADA'))}
                            style={{ width: '90px', padding: '3px 5px', fontSize: '0.78rem', border: '1px solid #ced4da', borderRadius: '3px', background: '#fff' }} />
                      </span>
                    : (getE('HORA DE LLEGADA') || "")}
            </td>
            <td style={{ textAlign: 'center' }}>
                {e != null && (
                    eEditable && editing
                        ? <input type="checkbox" ref={eTerminadoRef} defaultChecked={!!getE('TERMINADO')} />
                        : <i className={getE('TERMINADO') ? 'pi pi-check-circle' : 'pi pi-times-circle'}
                             style={{ color: getE('TERMINADO') ? '#107C10' : '#C50F1F', fontSize: '1rem' }} />
                )}
            </td>
            <td className="td-num">{e ? fmt$(row.eCosto) : ""}</td>

            {/* SALIDA */}
            <td style={{ fontSize: '0.75rem', color: '#555' }}>{row.sBitacoras.join(", ")}</td>
            <td>{sEditable && editing ? <input type="number" ref={sUnidadRef} defaultValue={getS('UNIDAD')} style={inNum} /> : (getS('UNIDAD') || "")}</td>
            <td>{sEditable && editing ? <input type="number" ref={sAforoRef}  defaultValue={getS('AFORO')}  style={inNum} /> : (getS('AFORO')  || "")}</td>
            <td style={{ textAlign: 'center' }}>
                {s != null && (
                    sEditable && editing
                        ? <input type="checkbox" ref={sTerminadoRef} defaultChecked={!!getS('TERMINADO')} />
                        : <i className={getS('TERMINADO') ? 'pi pi-check-circle' : 'pi pi-times-circle'}
                             style={{ color: getS('TERMINADO') ? '#107C10' : '#C50F1F', fontSize: '1rem' }} />
                )}
            </td>
            <td className="td-num">{s ? fmt$(row.sCosto) : ""}</td>
            <td className="td-num td-total">{total ? fmt$(total) : ""}</td>

            {/* ACCIONES */}
            {canEditMain && (
                <td style={{ textAlign: 'center' }}>
                    {canEdit && (
                        <Button
                            icon={editing ? 'pi pi-check' : 'pi pi-pencil'}
                            size="small"
                            severity={editing ? 'success' : 'info'}
                            rounded text
                            tooltip={editing ? 'Guardar' : 'Editar'}
                            tooltipOptions={{ position: 'left' }}
                            onClick={editing ? handleSave : handleToggle}
                        />
                    )}
                </td>
            )}
        </tr>
    );
});

const Tabla = ({ data, noEjecutados, onGuardar }) => {
    const [filtro, setFiltro] = useState("");
    const rows = useMemo(() => agrupar(data ?? []), [data]);
    const userData = useGetDataUser();
    const canEditMain = PERMISOS_EDICION.includes(userData?.user?.ID);
    const [editsMain, setEditsMain] = useState({});
    const [editingRowsMain, setEditingRowsMain] = useState(new Set());
    const rowsFiltradas = useMemo(() => {
        const q = filtro.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            (r.RUTA ?? "").toLowerCase().includes(q) ||
            ((DIAS[r.DIA] ?? r.DIA) ?? "").toLowerCase().includes(q) ||
            (r.FECHA ?? "").toLowerCase().includes(q) ||
            (r.TIPO_UNIDAD ?? "").toLowerCase().includes(q) ||
            String(r.e?.UNIDAD ?? "").includes(q) ||
            String(r.s?.UNIDAD ?? "").includes(q) ||
            (Array.isArray(r.eBitacoras) ? r.eBitacoras : []).some(b => String(b ?? "").toLowerCase().includes(q)) ||
            (Array.isArray(r.sBitacoras) ? r.sBitacoras : []).some(b => String(b ?? "").toLowerCase().includes(q))
        );
    }, [rows, filtro]);
   
    
    const spans = useMemo(() => calcSpans(rowsFiltradas), [rowsFiltradas]);
    const [openModal, setOpenModal] = useState(false);
    const [showRowsNE, setShowRowsNE] = useState(false);
    const [isPending, startTransition] = useTransition();

    // ── No ejecutados ─────────────────────────────────────────────
    const [filtroNE, setFiltroNE] = useState("");
    const [editsNE, setEditsNE] = useState({});
    const [editingRowsNE, setEditingRowsNE] = useState(new Set());

    const rowsNE = useMemo(() => agrupar(noEjecutados ?? []), [noEjecutados]);
    const rowsNEFiltradas = useMemo(() => {
        const q = filtroNE.trim().toLowerCase();
        if (!q) return rowsNE;
        return rowsNE.filter((r) =>
            (r.RUTA ?? "").toLowerCase().includes(q) ||
            ((DIAS[r.DIA] ?? r.DIA) ?? "").toLowerCase().includes(q) ||
            (r.FECHA ?? "").toLowerCase().includes(q) ||
            (Array.isArray(r.eBitacoras) ? r.eBitacoras : []).some(b => String(b ?? "").toLowerCase().includes(q)) ||
            (Array.isArray(r.sBitacoras) ? r.sBitacoras : []).some(b => String(b ?? "").toLowerCase().includes(q))
        );
    }, [rowsNE, filtroNE]);

    // ── Scroll virtual (NE) ──────────────────────────────────────────
    const neScrollRef = useRef(null);
    const rowVirtualizerNE = useVirtualizer({
        count: showRowsNE ? rowsNEFiltradas.length : 0,
        getScrollElement: () => neScrollRef.current,
        estimateSize: () => 42,
        overscan: 12,
    });

    const exportExcel = () => {
        // ── Totales de resumen ────────────────────────────────────────
        const totalEntradas = rowsFiltradas.reduce((sum, r) => sum + r.eCosto, 0);
        const totalSalidas = rowsFiltradas.reduce((sum, r) => sum + r.sCosto, 0);
        const granTotal = totalEntradas + totalSalidas;

        // Las filas de encabezado y datos inician después de 2 filas de resumen
        const OFF = 2;

        // ── Paleta (sin #) ────────────────────────────────────────────
        const C = {
            // Encabezado grupo base
            grpBg: "F2F3F3",
            grpTxt: "363432",
            // Entrada = azul Microsoft
            entrada: "0078D4",
            entradaSub: "106EBE",
            entradaTxt: "FFFFFF",
            entradaSubTxt: "F3F9FD",
            // Salida = violeta
            salida: "7B5EA7",
            salidaSub: "6A4F93",
            salidaTxt: "FFFFFF",
            salidaSubTxt: "F3EEFF",
            // Total = verde Microsoft
            total: "107C10",
            totalBg: "DFF6DD",
            totalTxt: "107C10",
            // DÍA = azul claro
            diaBg: "DEECF9",
            diaTxt: "005A9E",
            // Sub-encabezados neutros
            sub: "E5E8E7",
            subTxt: "605E5C",
            // Datos
            white: "FFFFFF",
            even: "FAFAFA",
            border: "E5E9F0",
            darkBorder: "D1D5DB",
        };

        const bdr = (rgb) => ({ style: "thin", color: { rgb } });
        const allBorders = (rgb) => ({ top: bdr(rgb), bottom: bdr(rgb), left: bdr(rgb), right: bdr(rgb) });

        const hStyle = (bg, txt = C.grpTxt) => ({
            fill: { patternType: "solid", fgColor: { rgb: bg } },
            font: { bold: true, color: { rgb: txt }, sz: 9, name: "Calibri" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: allBorders(C.darkBorder),
        });

        const subStyle = (bg, txt) => ({
            fill: { patternType: "solid", fgColor: { rgb: bg } },
            font: { bold: true, color: { rgb: txt }, sz: 8, name: "Calibri" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: allBorders(C.darkBorder),
        });

        const currencyFmt = '"$"#,##0.00';

        // ── Datos ─────────────────────────────────────────────────────
        const sheetData = [
            // Fila 0: Resumen de totales
            ["RESUMEN DE COSTOS", "", "", "", "", "",
                "TOTAL ENTRADAS", "", "", totalEntradas, "",
                "TOTAL SALIDAS", "", totalSalidas, "",
                granTotal],
            // Fila 1: separador
            ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""],
            // Fila 2: encabezados principales
            ["DÍA", "FECHA", "TURNO", "TIPO DE UNIDAD", "RUTA", "EXCEPCIÓN",
                "SERVICIO DE ENTRADA", "", "", "", "",
                "SERVICIO DE SALIDA", "", "", "",
                "TOTAL DE COSTO POR RUTA"],
            // Fila 3: sub-encabezados
            ["", "", "", "", "", "",
                "BITÁCORA", "UNIDAD", "AFORO", "HORA DE LLEGADA", "COSTO",
                "BITÁCORA", "UNIDAD", "AFORO", "COSTO",
                ""],
            // Filas 4+: datos
            ...rowsFiltradas.map((r) => {
                const { e, s } = r;
                const total = r.eCosto + r.sCosto;
                return [
                    DIAS[r.DIA] ?? r.DIA, r.FECHA, `Turno ${r.turnoNum}`, r.TIPO_UNIDAD, r.RUTA,
                    r.excepcion ? "EXCEPCIÓN" : "",
                    r.eBitacoras.join(", "), e?.UNIDAD ?? "", e?.AFORO ?? "", e?.["HORA DE LLEGADA"] ?? "", e ? r.eCosto || "" : "",
                    r.sBitacoras.join(", "), s?.UNIDAD ?? "", s?.AFORO ?? "", s ? r.sCosto || "" : "",
                    total || "",
                ];
            }),
        ];

        const ws = XLSX.utils.aoa_to_sheet(sheetData);

        // ── Merges ────────────────────────────────────────────────────
        const merges = [
            // Fila resumen (row 0)
            { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },   // "RESUMEN DE COSTOS"
            { s: { r: 0, c: 6 }, e: { r: 0, c: 8 } },   // "TOTAL ENTRADAS" label
            { s: { r: 0, c: 11 }, e: { r: 0, c: 12 } }, // "TOTAL SALIDAS" label
            // Headers (OFF = 2, OFF+1 = 3)
            { s: { r: OFF, c: 0 }, e: { r: OFF + 1, c: 0 } }, { s: { r: OFF, c: 1 }, e: { r: OFF + 1, c: 1 } },
            { s: { r: OFF, c: 2 }, e: { r: OFF + 1, c: 2 } }, { s: { r: OFF, c: 3 }, e: { r: OFF + 1, c: 3 } },
            { s: { r: OFF, c: 4 }, e: { r: OFF + 1, c: 4 } }, { s: { r: OFF, c: 5 }, e: { r: OFF + 1, c: 5 } },
            { s: { r: OFF, c: 6 }, e: { r: OFF, c: 10 } },
            { s: { r: OFF, c: 11 }, e: { r: OFF, c: 14 } },
            { s: { r: OFF, c: 15 }, e: { r: OFF + 1, c: 15 } },
        ];
        let i = 0;
        while (i < rowsFiltradas.length) {
            let j = i + 1;
            while (j < rowsFiltradas.length && rowsFiltradas[j].DIA === rowsFiltradas[i].DIA) j++;
            if (j - i > 1) merges.push({ s: { r: OFF + 2 + i, c: 0 }, e: { r: OFF + 2 + j - 1, c: 0 } });
            i = j;
        }
        ws["!merges"] = merges;

        // ── Anchos y alto de filas ────────────────────────────────────
        ws["!cols"] = [
            { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 55 },
            { wch: 14 },
            { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 22 },
            { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 22 },
            { wch: 22 },
        ];
        ws["!rows"] = [
            { hpt: 22 }, // resumen
            { hpt: 6 }, // separador
            { hpt: 24 }, // encabezado grupo
            { hpt: 18 }, // sub-encabezado
        ];

        const enc = (r, c) => XLSX.utils.encode_cell({ r, c });

        // ── Estilos: fila 0 (resumen de totales) ─────────────────────
        // Cols 0-5: título "RESUMEN DE COSTOS"
        for (let c = 0; c <= 5; c++) {
            const addr = enc(0, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = hStyle(C.grpBg, C.grpTxt);
        }
        // Cols 6-8: label "TOTAL ENTRADAS"
        for (let c = 6; c <= 8; c++) {
            const addr = enc(0, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = hStyle(C.entrada, C.entradaTxt);
        }
        // Col 9: valor total entradas
        {
            const addr = enc(0, 9);
            ws[addr] = { v: totalEntradas, t: "n" };
            ws[addr].s = {
                fill: { patternType: "solid", fgColor: { rgb: C.entradaSub } },
                font: { bold: true, sz: 10, name: "Calibri", color: { rgb: C.entradaSubTxt } },
                alignment: { horizontal: "right", vertical: "center" },
                border: allBorders(C.darkBorder),
                numFmt: currencyFmt,
            };
        }
        // Col 10: vacío (bitácoras entrada — no aplica en resumen)
        {
            const addr = enc(0, 10);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = hStyle(C.grpBg, C.grpTxt);
        }
        // Cols 11-12: label "TOTAL SALIDAS"
        for (let c = 11; c <= 12; c++) {
            const addr = enc(0, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = hStyle(C.salida, C.salidaTxt);
        }
        // Col 13: valor total salidas
        {
            const addr = enc(0, 13);
            ws[addr] = { v: totalSalidas, t: "n" };
            ws[addr].s = {
                fill: { patternType: "solid", fgColor: { rgb: C.salidaSub } },
                font: { bold: true, sz: 10, name: "Calibri", color: { rgb: C.salidaSubTxt } },
                alignment: { horizontal: "right", vertical: "center" },
                border: allBorders(C.darkBorder),
                numFmt: currencyFmt,
            };
        }
        // Col 14: vacío (bitácoras salida)
        {
            const addr = enc(0, 14);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = hStyle(C.grpBg, C.grpTxt);
        }
        // Col 15: gran total
        {
            const addr = enc(0, 15);
            ws[addr] = { v: granTotal, t: "n" };
            ws[addr].s = {
                fill: { patternType: "solid", fgColor: { rgb: C.total } },
                font: { bold: true, sz: 11, name: "Calibri", color: { rgb: C.entradaTxt } },
                alignment: { horizontal: "right", vertical: "center" },
                border: allBorders(C.darkBorder),
                numFmt: currencyFmt,
            };
        }

        // ── Estilos: fila 1 (separador) ───────────────────────────────
        for (let c = 0; c < 16; c++) {
            const addr = enc(1, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = { fill: { patternType: "solid", fgColor: { rgb: C.white } } };
        }

        // ── Estilos: fila OFF (grupos) ────────────────────────────────
        for (let c = 0; c < 16; c++) {
            const addr = enc(OFF, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            if (c >= 6 && c <= 10) ws[addr].s = hStyle(C.entrada, C.entradaTxt);
            else if (c >= 11 && c <= 14) ws[addr].s = hStyle(C.salida, C.salidaTxt);
            else if (c === 15) ws[addr].s = hStyle(C.total, C.entradaTxt);
            else ws[addr].s = hStyle(C.grpBg, C.grpTxt);
        }

        // ── Estilos: fila OFF+1 (sub-encabezados) ────────────────────
        for (let c = 0; c < 16; c++) {
            const addr = enc(OFF + 1, c);
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            if (c >= 6 && c <= 10) ws[addr].s = subStyle(C.entradaSub, C.entradaSubTxt);
            else if (c >= 11 && c <= 14) ws[addr].s = subStyle(C.salidaSub, C.salidaSubTxt);
            else ws[addr].s = subStyle(C.sub, C.subTxt);
        }

        // ── Estilos: filas de datos ───────────────────────────────────
        for (let row = 0; row < rowsFiltradas.length; row++) {
            const r = rowsFiltradas[row];
            const bg = r.excepcion ? "FFF3CD" : (row % 2 === 0 ? C.white : C.even);
            for (let c = 0; c < 16; c++) {
                const addr = enc(OFF + 2 + row, c);
                if (!ws[addr]) ws[addr] = { v: "", t: "s" };

                if (c === 0) {
                    // Celda DÍA
                    ws[addr].s = {
                        fill: { patternType: "solid", fgColor: { rgb: C.diaBg } },
                        font: { bold: true, sz: 9, name: "Calibri", color: { rgb: C.diaTxt } },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: allBorders(C.darkBorder),
                    };
                } else if (c === 5) {
                    // Celda EXCEPCIÓN
                    ws[addr].s = {
                        fill: { patternType: "solid", fgColor: { rgb: r.excepcion ? "FFC107" : bg } },
                        font: { bold: r.excepcion, sz: 9, name: "Calibri", color: { rgb: r.excepcion ? "856404" : C.grpTxt } },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: allBorders(C.border),
                    };
                } else if (c === 15) {
                    // Celda TOTAL por fila
                    const totalFila = r.eCosto + r.sCosto;
                    if (totalFila) { ws[addr].v = totalFila; ws[addr].t = "n"; }
                    ws[addr].s = {
                        fill: { patternType: "solid", fgColor: { rgb: C.totalBg } },
                        font: { bold: true, sz: 9, name: "Calibri", color: { rgb: C.totalTxt } },
                        alignment: { horizontal: "right", vertical: "center" },
                        border: allBorders(C.border),
                        ...(ws[addr].t === "n" ? { numFmt: currencyFmt } : {}),
                    };
                } else {
                    const isNum = [10, 14].includes(c); // columnas COSTO
                    const isBit = [6, 11].includes(c); // columnas BITÁCORAS
                    if (isNum && ws[addr].v !== "" && ws[addr].v != null) ws[addr].t = "n";
                    ws[addr].s = {
                        fill: { patternType: "solid", fgColor: { rgb: bg } },
                        font: { sz: 9, name: "Calibri", color: { rgb: C.grpTxt } },
                        alignment: {
                            horizontal: isNum ? "right" : c === 4 ? "left" : "center",
                            vertical: "center",
                            wrapText: c === 4 || isBit,
                        },
                        border: allBorders(C.border),
                        ...(isNum && ws[addr].t === "n" ? { numFmt: currencyFmt } : {}),
                    };
                }
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Facturación");
        XLSX.writeFile(wb, "servicios_facturacion.xlsx");
    };

    if (!data?.length)
        return <p className="tabla-facturacion__empty">Sin datos para mostrar.</p>;

    return (
        <div className="tabla-facturacion">
            <div className="tabla-facturacion__toolbar">
                <span className="p-input-icon-left">

                    <InputText
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        placeholder="Buscar "
                        size="small"
                        style={{ width: "260px" }}
                    />
                </span>
                <div style={{ display: 'flex', gap: '20px' }}>
                    {/* <Button

                        icon="pi pi-info-circle"
                        size="small"
                        severity="warning"
                        label="No ejecutadas"
                        loading={isPending}
                        onClick={() => setOpenModal(true)}
                    /> */}
                    <Button

                        icon="pi pi-file-excel"
                        size="small"
                        severity="success"

                        onClick={exportExcel}
                    />
                </div>
            </div>
            {/* ── Resumen de totales ──────────────────────────────── */}
            {(() => {
                const totalEntradas  = rowsFiltradas.reduce((s, r) => s + r.eCosto, 0);
                const totalSalidas   = rowsFiltradas.reduce((s, r) => s + r.sCosto, 0);
                const granTotal      = totalEntradas + totalSalidas;
                const totalBitacoras = rowsFiltradas.reduce((s, r) => s + r.eBitacoras.length + r.sBitacoras.length, 0);
                const card = (label, value, color, icon) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${color}20`, borderRadius: 6, padding: '8px 16px', minWidth: 180, boxShadow: '0 1px 4px #0001' }}>
                        <i className={icon} style={{ fontSize: '1.3rem', color }} />
                        <div>
                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                            <div style={{ fontSize: '1rem', fontWeight: 700, color }}>{value}</div>
                        </div>
                    </div>
                );
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '10px 0' }}>
                        {card('Total entradas',   fmt$(totalEntradas),  '#0078D4', 'pi pi-sign-in')}
                        {card('Total salidas',    fmt$(totalSalidas),   '#7B5EA7', 'pi pi-sign-out')}
                        {card('Gran total',       fmt$(granTotal),      '#107C10', 'pi pi-dollar')}
                        {card('Total bitácoras',  totalBitacoras,       '#C75100', 'pi pi-book')}
                    </div>
                );
            })()}
            <div className="tabla-facturacion__scroll">
                <table>
                    <thead>
                        <tr>
                            <th rowSpan={2}>DÍA</th>
                            <th rowSpan={2}>FECHA</th>
                            <th rowSpan={2}>TURNO</th>
                            <th rowSpan={2}>TIPO DE UNIDAD</th>
                            <th rowSpan={2} className="th-ruta">RUTA</th>
                            <th rowSpan={2}>EXCEPCIÓN</th>
                            <th colSpan={6} className="th-group th-entrada">SERVICIO DE ENTRADA</th>
                            <th colSpan={5} className="th-group th-salida">SERVICIO DE SALIDA</th>
                            <th rowSpan={2} className="th-total">TOTAL COSTO</th>
                            {canEditMain && <th rowSpan={2}>ACCIONES</th>}
                        </tr>
                        <tr>
                            <th className="th-entrada">BITÁCORA</th>
                            <th className="th-entrada">UNIDAD</th>
                            <th className="th-entrada">AFORO</th>
                            <th className="th-entrada">HORA LLEGADA</th>
                            <th className="th-entrada">TERMINADO</th>
                            <th className="th-entrada">COSTO</th>

                            <th className="th-salida">BITÁCORA</th>
                            <th className="th-salida">UNIDAD</th>
                            <th className="th-salida">AFORO</th>
                            <th className="th-salida">TERMINADO</th>
                            <th className="th-salida">COSTO</th>

                        </tr>
                    </thead>
                    <tbody>
                        {rowsFiltradas.map((row, i) => {
                            const rKey = `${row.DIA}|${row.FECHA}|${row.RUTA}|${row.turnoNum}|${row.e?.BITACORA ?? ""}|${row.s?.BITACORA ?? ""}`;
                            return (
                                <MainRow
                                    key={rKey}
                                    row={row}
                                    spanDia={spans[i]}
                                    editing={editingRowsMain.has(rKey)}
                                    savedE={editsMain[rKey]?.e}
                                    savedS={editsMain[rKey]?.s}
                                    setEditsMain={setEditsMain}
                                    setEditingRowsMain={setEditingRowsMain}
                                    onGuardar={onGuardar}
                                    canEditMain={canEditMain}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog
                header="Servicios no ejecutados"
                visible={openModal}
                onShow={() => startTransition(() => setShowRowsNE(true))}
                onHide={() => { setOpenModal(false); setFiltroNE(""); setEditingRowsNE(new Set()); setShowRowsNE(false); }}
                style={{ width: '95vw' }}
                maximizable
            >
                <div className="tabla-facturacion" style={{ marginTop: 0, border: 'none', boxShadow: 'none' }}>
                    <div className="tabla-facturacion__toolbar">
                        <InputText
                            value={filtroNE}
                            onChange={(ev) => setFiltroNE(ev.target.value)}
                            placeholder="Buscar..."
                            size="small"
                            style={{ width: '260px' }}
                        />
                    </div>

                    {(!showRowsNE || isPending)
                        ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0078D4' }} />
                            </div>
                        )
                        : rowsNEFiltradas.length === 0
                        ? <p className="tabla-facturacion__empty">Sin servicios no ejecutados.</p>
                        : (
                            <div ref={neScrollRef} className="tabla-facturacion__scroll" style={{ maxHeight: '65vh', overflow: 'auto' }}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th rowSpan={2}>DÍA</th>
                                            <th rowSpan={2}>FECHA</th>
                                            <th rowSpan={2}>TURNO</th>
                                            <th rowSpan={2}>TIPO DE UNIDAD</th>
                                            <th rowSpan={2} className="th-ruta">RUTA</th>
                                            <th colSpan={5} className="th-group th-entrada">SERVICIO DE ENTRADA</th>
                                            <th colSpan={4} className="th-group th-salida">SERVICIO DE SALIDA</th>
                                            <th rowSpan={2} className="th-total">TOTAL COSTO</th>
                                            <th rowSpan={2}>ACCIONES</th>
                                        </tr>
                                        <tr>
                                            <th className="th-entrada">UNIDAD</th>
                                            <th className="th-entrada">AFORO</th>
                                            <th className="th-entrada">HORA LLEGADA</th>
                                            <th className="th-entrada">TERMINADO</th>
                                            <th className="th-entrada">COSTO</th>
                                            <th className="th-salida">UNIDAD</th>
                                            <th className="th-salida">AFORO</th>
                                            <th className="th-salida">TERMINADO</th>
                                            <th className="th-salida">COSTO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Padding superior — espacio virtual por encima de las filas visibles */}
                                        {rowVirtualizerNE.getVirtualItems().length > 0 && (
                                            <tr style={{ height: rowVirtualizerNE.getVirtualItems()[0].start }}>
                                                <td colSpan={16} style={{ padding: 0, border: 'none' }} />
                                            </tr>
                                        )}
                                        {rowVirtualizerNE.getVirtualItems().map((vRow) => {
                                            const row = rowsNEFiltradas[vRow.index];
                                            const rKey = `${row.DIA}|${row.FECHA}|${row.RUTA}|${row.turnoNum}|${row.e?.BITACORA ?? ""}|${row.s?.BITACORA ?? ""}`;
                                            return (
                                                <NERow
                                                    key={rKey}
                                                    row={row}
                                                    editing={editingRowsNE.has(rKey)}
                                                    savedE={editsNE[rKey]?.e}
                                                    savedS={editsNE[rKey]?.s}
                                                    setEditsNE={setEditsNE}
                                                    setEditingRowsNE={setEditingRowsNE}
                                                    onGuardar={onGuardar}
                                                />
                                            );
                                        })}
                                        {/* Padding inferior — espacio virtual por debajo de las filas visibles */}
                                        {rowVirtualizerNE.getVirtualItems().length > 0 && (
                                            <tr style={{ height: rowVirtualizerNE.getTotalSize() - (rowVirtualizerNE.getVirtualItems().at(-1)?.end ?? 0) }}>
                                                <td colSpan={16} style={{ padding: 0, border: 'none' }} />
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )
                    }
                </div>
            </Dialog>
        </div>
    );
};

export default Tabla;